import React, { useState, useMemo } from 'react';
import Badge from 'react-bootstrap/Badge';
import Button from 'react-bootstrap/Button';
import Card from 'react-bootstrap/Card';
import Form from 'react-bootstrap/Form';
import Modal from 'react-bootstrap/Modal';
import Pagination from 'react-bootstrap/Pagination';
import Spinner from 'react-bootstrap/Spinner';
import Table from 'react-bootstrap/Table';
import { Link } from '@tanstack/react-location';
import { useChargeAttemptsQuery } from '../../queries/payment.query';
import { useResendDelinquentEmailsMutation } from '../../mutations/payment.mutation';
import { ChargeAttemptsFilters as FiltersType, ChargeSource } from '../../types/payment.types';
import CardTypeIcon from './CardTypeIcon';
import ChargeAttemptsFilters from './ChargeAttemptsFilters';

const formatSource = (source: ChargeSource): string => {
  const sourceLabels: Record<ChargeSource, string> = {
    Unknown: '-',
    Manual: 'Manual',
    ChargeAll: 'Charge All',
    Monitoring: 'Monitoring',
    GenerateInvoices: 'Gen. Invoices',
    External: 'External'
  };
  return sourceLabels[source] || source;
};

const getSourceBadgeVariant = (source: ChargeSource): string => {
  const variants: Record<ChargeSource, string> = {
    Unknown: 'secondary',
    Manual: 'info',
    ChargeAll: 'primary',
    Monitoring: 'warning',
    GenerateInvoices: 'dark',
    External: 'light'
  };
  return variants[source] || 'secondary';
};

function ChargeAttemptsList() {
  const [filters, setFilters] = useState<FiltersType>({ page: 1, pageSize: 15 });
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  const { data, isLoading } = useChargeAttemptsQuery(filters);
  const resendMutation = useResendDelinquentEmailsMutation();

  // Get failed ChargeAll attempts from current page (only these are eligible for resend)
  const failedAttempts = useMemo(() => {
    return (
      data?.chargeAttempts.filter(
        (attempt) => !attempt.success && attempt.source === 'ChargeAll'
      ) ?? []
    );
  }, [data?.chargeAttempts]);

  // Check if all failed attempts on current page are selected
  const allFailedSelected = useMemo(() => {
    return failedAttempts.length > 0 && failedAttempts.every((a) => selectedIds.has(a.id));
  }, [failedAttempts, selectedIds]);

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      const newSelected = new Set(selectedIds);
      failedAttempts.forEach((a) => newSelected.add(a.id));
      setSelectedIds(newSelected);
    } else {
      const newSelected = new Set(selectedIds);
      failedAttempts.forEach((a) => newSelected.delete(a.id));
      setSelectedIds(newSelected);
    }
  };

  const handleSelectOne = (id: string, checked: boolean) => {
    const newSelected = new Set(selectedIds);
    if (checked) {
      newSelected.add(id);
    } else {
      newSelected.delete(id);
    }
    setSelectedIds(newSelected);
  };

  const handleResendEmails = () => {
    setShowConfirmModal(true);
  };

  const handleConfirmResend = () => {
    resendMutation.mutate(
      { chargeAttemptIds: Array.from(selectedIds) },
      {
        onSuccess: () => {
          setSelectedIds(new Set());
          setShowConfirmModal(false);
        },
        onError: () => {
          setShowConfirmModal(false);
        }
      }
    );
  };

  const handleApplyFilters = (newFilters: FiltersType) => {
    setFilters({ ...newFilters, page: 1, pageSize: filters.pageSize });
    setSelectedIds(new Set());
  };

  const handleClearFilters = () => {
    setFilters({ page: 1, pageSize: filters.pageSize });
    setSelectedIds(new Set());
  };

  const handlePageChange = (page: number) => {
    setFilters({ ...filters, page });
  };

  const handlePageSizeChange = (newPageSize: number) => {
    setFilters({ ...filters, page: 1, pageSize: newPageSize });
    setSelectedIds(new Set());
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  return (
    <Card className="mb-4">
      <Card.Header className="d-flex justify-content-between align-items-center">
        <h5 className="mb-0">Charge Attempts</h5>
        {selectedIds.size > 0 && (
          <Button
            variant="warning"
            size="sm"
            onClick={handleResendEmails}
            disabled={resendMutation.isLoading}>
            {resendMutation.isLoading ? (
              <>
                <Spinner animation="border" size="sm" className="me-1" />
                Sending...
              </>
            ) : (
              `Resend Emails (${selectedIds.size})`
            )}
          </Button>
        )}
      </Card.Header>
      <Card.Body>
        <ChargeAttemptsFilters onApply={handleApplyFilters} onClear={handleClearFilters} />

        {isLoading ? (
          <div className="text-center py-4">
            <Spinner animation="border" />
          </div>
        ) : (
          <>
            <div style={{ maxHeight: '500px', overflowY: 'auto' }}>
              <Table responsive hover size="sm">
                <thead style={{ position: 'sticky', top: 0, background: 'white', zIndex: 1 }}>
                  <tr>
                    <th style={{ width: '40px' }}>
                      <Form.Check
                        type="checkbox"
                        checked={allFailedSelected}
                        onChange={(e) => handleSelectAll(e.target.checked)}
                        disabled={failedAttempts.length === 0}
                        title="Select all failed attempts on this page"
                      />
                    </th>
                    <th>Date</th>
                    <th>Client</th>
                    <th>Amount</th>
                    <th>Status</th>
                    <th>Source</th>
                    <th>Card</th>
                    <th>Card Number</th>
                    <th>Invoice #</th>
                  </tr>
                </thead>
                <tbody>
                  {data?.chargeAttempts.map((attempt) => (
                    <tr key={attempt.id}>
                      <td>
                        {!attempt.success && attempt.source === 'ChargeAll' ? (
                          <Form.Check
                            type="checkbox"
                            checked={selectedIds.has(attempt.id)}
                            onChange={(e) => handleSelectOne(attempt.id, e.target.checked)}
                          />
                        ) : null}
                      </td>
                      <td>{formatDate(attempt.attemptDate)}</td>
                      <td>
                        {attempt.clientId ? (
                          <Link
                            to={`/clients/${attempt.clientId}`}
                            className="text-decoration-none">
                            {attempt.clientName}
                          </Link>
                        ) : (
                          attempt.clientName
                        )}
                      </td>
                      <td>{formatCurrency(attempt.amount)}</td>
                      <td>
                        <Badge bg={attempt.success ? 'success' : 'danger'}>
                          {attempt.success ? 'Success' : 'Failed'}
                        </Badge>
                        {attempt.refundedAt && (
                          <Badge bg="warning" className="ms-1">
                            Refunded
                          </Badge>
                        )}
                      </td>
                      <td>
                        <Badge
                          bg={getSourceBadgeVariant(attempt.source)}
                          text={attempt.source === 'External' ? 'dark' : undefined}>
                          {formatSource(attempt.source)}
                        </Badge>
                      </td>
                      <td>
                        <CardTypeIcon cardType={attempt.cardType} size={20} />
                      </td>
                      <td className="font-monospace small">{attempt.maskedCardNumber || '-'}</td>
                      <td>{attempt.invoiceFreshbooksId || '-'}</td>
                    </tr>
                  ))}
                  {data?.chargeAttempts.length === 0 && (
                    <tr>
                      <td colSpan={9} className="text-center text-muted py-4">
                        No charge attempts found
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>

            {data && (
              <div className="d-flex justify-content-between align-items-center mt-3">
                <div className="d-flex align-items-center gap-3">
                  <small className="text-muted">
                    Showing page {data.currentPage} of {data.numberOfPages} ({data.totalCount}{' '}
                    total)
                  </small>
                  <div className="d-flex align-items-center gap-2">
                    <Form.Label className="small mb-0 text-muted">Per page:</Form.Label>
                    <Form.Select
                      size="sm"
                      style={{ width: 'auto' }}
                      value={filters.pageSize}
                      onChange={(e) => handlePageSizeChange(Number(e.target.value))}>
                      <option value={15}>15</option>
                      <option value={25}>25</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                      <option value={200}>200</option>
                    </Form.Select>
                  </div>
                </div>
                {data.numberOfPages > 1 && (
                  <Pagination size="sm" className="mb-0">
                    <Pagination.First
                      onClick={() => handlePageChange(1)}
                      disabled={data.currentPage === 1}
                    />
                    <Pagination.Prev
                      onClick={() => handlePageChange(data.currentPage - 1)}
                      disabled={data.currentPage === 1}
                    />
                    {(() => {
                      const pages: number[] = [];
                      const maxVisible = 5;
                      let start = Math.max(1, data.currentPage - Math.floor(maxVisible / 2));
                      const end = Math.min(data.numberOfPages, start + maxVisible - 1);
                      start = Math.max(1, end - maxVisible + 1);
                      for (let i = start; i <= end; i++) {
                        pages.push(i);
                      }
                      return pages.map((pageNum) => (
                        <Pagination.Item
                          key={pageNum}
                          active={pageNum === data.currentPage}
                          onClick={() => handlePageChange(pageNum)}>
                          {pageNum}
                        </Pagination.Item>
                      ));
                    })()}
                    <Pagination.Next
                      onClick={() => handlePageChange(data.currentPage + 1)}
                      disabled={data.currentPage === data.numberOfPages}
                    />
                    <Pagination.Last
                      onClick={() => handlePageChange(data.numberOfPages)}
                      disabled={data.currentPage === data.numberOfPages}
                    />
                  </Pagination>
                )}
              </div>
            )}
          </>
        )}
      </Card.Body>

      <Modal show={showConfirmModal} onHide={() => setShowConfirmModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Resend Emails</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            Are you sure you want to resend delinquent emails for{' '}
            <strong>{selectedIds.size}</strong> failed charge attempt
            {selectedIds.size === 1 ? '' : 's'}?
          </p>
          <p className="text-muted small mb-0">
            This will send an email with the invoice PDF to each client, notifying them that their
            card was declined.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowConfirmModal(false)}>
            Cancel
          </Button>
          <Button
            variant="warning"
            onClick={handleConfirmResend}
            disabled={resendMutation.isLoading}>
            {resendMutation.isLoading ? (
              <>
                <Spinner animation="border" size="sm" className="me-1" />
                Sending...
              </>
            ) : (
              'Resend Emails'
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </Card>
  );
}

export default ChargeAttemptsList;
